import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styles from "./UpdateProjectForm.module.css";
import Button from "../../../components/UI/Button/Button";
import MultiSelectDropdown from "../../../components/UI/MultiSelectDropdown/MultiSelectDropdown";
import FeedbackModal from "../../../components/UI/FeedbackModal/FeedbackModal";
import {
  getAllProjects,
  updateProject,
  uploadProjectImage,
} from "../../../services/client-project-service";

const technologyOptions = [
  "HTML",
  "CSS",
  "JavaScript",
  "React",
  "ReactNative",
  "NodeJs",
  "Express",
  "MongoDB",
  "MySQL",
];

const websiteTypeOptions = [
  "E-commerce / Online Store",
  "Marketplace",
  "Business / Corporate Website",
  "News & Magazine",
  "Blog",
  "Video Streaming",
  "Portfolio Site",
  "Social Networks",
  "Community Forums",
  "E-learning Platform",
  "Health & Fitness",
  "Doctor Appointment / Medical Service Sites",
  "Travel & Booking",
  "Food Delivery Platforms",
  "Job Boards & Career Sites",
  "Banking & Financial Institutions",
  "Investment Platforms",
  "Nonprofit / Charity Websites",
  "App Landing Pages",
  "SaaS Product Websites",
];

export default function UpdateProjectForm() {
  const { t } = useTranslation("common");
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("info");
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");

  const projectImageInputRef = useRef(null);
  const desktopImageInputRef = useRef(null);
  const mobileImageInputRef = useRef(null);
  const [projectImagePreview, setProjectImagePreview] = useState("");
  const [desktopImagePreview, setDesktopImagePreview] = useState("");
  const [mobileImagePreview, setMobileImagePreview] = useState("");
  const [projectImageFile, setProjectImageFile] = useState(null);
  const [desktopImageFile, setDesktopImageFile] = useState(null);
  const [mobileImageFile, setMobileImageFile] = useState(null);

  const [formData, setFormData] = useState({
    type: "web",
    title: "",
    description: "",
    techStack: [],
    projectImage: "",
    desktopModalImage: "",
    mobileModalImage: "",
    link: "",
    websiteUrl: "",
    websiteType: "",
  });

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setIsLoading(true);
    try {
      const result = await getAllProjects();
      if (result.success) {
        setProjects(result.data || []);
      } else {
        setModalType("error");
        setModalTitle("Error");
        setModalMessage(result.error || "Failed to load projects");
        setModalOpen(true);
      }
    } catch (error) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage("Failed to load projects");
      setModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProjectSelect = (e) => {
    const projectId = e.target.value;
    const project = projects.find((p) => p._id === projectId);
    if (project) {
      setSelectedProject(project);
      const techStack = project.techStack
        ? typeof project.techStack === "string"
          ? project.techStack.split(", ").filter(Boolean)
          : project.techStack
        : [];

      setFormData({
        type: project.type || "web",
        title: project.title || "",
        description: project.description || "",
        techStack: techStack,
        projectImage: project.projectImage || "",
        desktopModalImage: project.desktopModalImage || "",
        mobileModalImage: project.mobileModalImage || "",
        link: project.link || "",
        websiteUrl: project.websiteUrl || "",
        websiteType: project.websiteType || "",
      });

      setProjectImagePreview(project.projectImage || "");
      setDesktopImagePreview(project.desktopModalImage || "");
      setMobileImagePreview(project.mobileModalImage || "");
      setProjectImageFile(null);
      setDesktopImageFile(null);
      setMobileImageFile(null);
    } else {
      setSelectedProject(null);
      setFormData({
        type: "web",
        title: "",
        description: "",
        techStack: [],
        projectImage: "",
        desktopModalImage: "",
        mobileModalImage: "",
        link: "",
        websiteUrl: "",
        websiteType: "",
      });
      setProjectImagePreview("");
      setDesktopImagePreview("");
      setMobileImagePreview("");
      setProjectImageFile(null);
      setDesktopImageFile(null);
      setMobileImageFile(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleTechStackChange = (selected) => {
    setFormData((p) => ({ ...p, techStack: selected }));
  };

  const handleProjectImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/gif",
    ];
    if (!allowedTypes.includes(file.type)) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage(
        "Please select a valid image file (JPEG, PNG, WebP, or GIF)",
      );
      setModalOpen(true);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage("File size must be less than 10MB");
      setModalOpen(true);
      return;
    }

    setProjectImageFile(file);
    const previewUrl = URL.createObjectURL(file);
    setProjectImagePreview(previewUrl);

    if (projectImageInputRef.current) {
      projectImageInputRef.current.value = "";
    }
  };

  const handleDesktopImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/gif",
    ];
    if (!allowedTypes.includes(file.type)) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage(
        "Please select a valid image file (JPEG, PNG, WebP, or GIF)",
      );
      setModalOpen(true);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage("File size must be less than 10MB");
      setModalOpen(true);
      return;
    }

    setDesktopImageFile(file);
    const previewUrl = URL.createObjectURL(file);
    setDesktopImagePreview(previewUrl);

    if (desktopImageInputRef.current) {
      desktopImageInputRef.current.value = "";
    }
  };

  const handleMobileImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/gif",
    ];
    if (!allowedTypes.includes(file.type)) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage(
        "Please select a valid image file (JPEG, PNG, WebP, or GIF)",
      );
      setModalOpen(true);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage("File size must be less than 10MB");
      setModalOpen(true);
      return;
    }

    setMobileImageFile(file);
    const previewUrl = URL.createObjectURL(file);
    setMobileImagePreview(previewUrl);

    if (mobileImageInputRef.current) {
      mobileImageInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProject) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage("Please select a project to update");
      setModalOpen(true);
      return;
    }

    setIsSubmitting(true);

    try {
      let projectImageUrl = formData.projectImage;
      let desktopModalImageUrl = formData.desktopModalImage;
      let mobileModalImageUrl = formData.mobileModalImage;

      const websiteName =
        formData.websiteUrl.trim() || formData.title.trim() || "project";

      if (projectImageFile) {
        const result = await uploadProjectImage(
          projectImageFile,
          websiteName,
          "main",
        );
        if (result.success && result.data?.imageUrl) {
          projectImageUrl = result.data.imageUrl;
        } else {
          throw new Error(result.message || "Failed to upload project image");
        }
      }

      if (desktopImageFile) {
        const result = await uploadProjectImage(
          desktopImageFile,
          websiteName,
          "desktop",
        );
        if (result.success && result.data?.imageUrl) {
          desktopModalImageUrl = result.data.imageUrl;
        } else {
          throw new Error(result.message || "Failed to upload desktop image");
        }
      }

      if (mobileImageFile) {
        const result = await uploadProjectImage(
          mobileImageFile,
          websiteName,
          "mobile",
        );
        if (result.success && result.data?.imageUrl) {
          mobileModalImageUrl = result.data.imageUrl;
        } else {
          throw new Error(result.message || "Failed to upload mobile image");
        }
      }

      const projectData = {
        type: formData.type,
        title: formData.title.trim(),
        description: formData.description.trim(),
        techStack: Array.isArray(formData.techStack)
          ? formData.techStack.join(", ")
          : formData.techStack,
        projectImage: projectImageUrl.trim(),
        desktopModalImage: desktopModalImageUrl.trim(),
        mobileModalImage: mobileModalImageUrl.trim(),
        link: formData.link.trim(),
        websiteUrl: formData.websiteUrl.trim(),
        websiteType: formData.websiteType.trim(),
      };

      const result = await updateProject(selectedProject._id, projectData);

      if (result.success) {
        setModalType("success");
        setModalTitle("Success");
        setModalMessage("Project updated successfully!");
        setModalOpen(true);
        await loadProjects();
        setSelectedProject(null);
        setFormData({
          type: "web",
          title: "",
          description: "",
          techStack: [],
          projectImage: "",
          desktopModalImage: "",
          mobileModalImage: "",
          link: "",
          websiteUrl: "",
          websiteType: "",
        });
        setProjectImagePreview("");
        setDesktopImagePreview("");
        setMobileImagePreview("");
        setProjectImageFile(null);
        setDesktopImageFile(null);
        setMobileImageFile(null);
      } else {
        throw new Error(result.error || "Failed to update project");
      }
    } catch (error) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage(error.message || "Failed to update project");
      setModalOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className={styles.projectForm}>
        <div className={styles.formGroup}>
          <label htmlFor="projectSelect" className={styles.label}>
            Select Project to Update *
          </label>
          <select
            id="projectSelect"
            name="projectSelect"
            value={selectedProject?._id || ""}
            onChange={handleProjectSelect}
            className={styles.select}
            required
            disabled={isLoading || isSubmitting}
          >
            <option value="">
              {isLoading ? "Loading projects..." : "Select a project..."}
            </option>
            {projects.map((project) => (
              <option key={project._id} value={project._id}>
                {project.title || project._id}
              </option>
            ))}
          </select>
        </div>

        {selectedProject && (
          <>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="type" className={styles.label}>
                  {t("forms.clientProject.labels.projectType")} *
                </label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className={styles.select}
                  required
                  disabled={isSubmitting}
                >
                  <option value="web">
                    {t("forms.clientProject.options.webApplication")}
                  </option>
                  <option value="mobile">
                    {t("forms.clientProject.options.mobileApplication")}
                  </option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="websiteType" className={styles.label}>
                  {t("forms.clientProject.labels.websiteType")} *
                </label>
                <select
                  id="websiteType"
                  name="websiteType"
                  value={formData.websiteType}
                  onChange={handleInputChange}
                  className={styles.select}
                  required
                  disabled={isSubmitting}
                >
                  <option value="">
                    {t("forms.clientProject.placeholders.selectWebsiteType")}
                  </option>
                  {websiteTypeOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="title" className={styles.label}>
                {t("forms.clientProject.labels.projectTitle")} *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={styles.input}
                placeholder={t(
                  "forms.clientProject.placeholders.enterProjectTitle",
                )}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="description" className={styles.label}>
                {t("forms.clientProject.labels.projectDescription")} *
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className={styles.textarea}
                placeholder={t(
                  "forms.clientProject.placeholders.describeProject",
                )}
                rows="4"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                {t("forms.clientProject.labels.technologyStack")}
              </label>
              <MultiSelectDropdown
                options={technologyOptions}
                selectedValues={formData.techStack}
                onChange={handleTechStackChange}
                placeholder={t(
                  "forms.clientProject.placeholders.selectTechnologies",
                )}
                className={styles.techStackDropdown}
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="link" className={styles.label}>
                  {t("forms.clientProject.labels.projectLink")} *
                </label>
                <input
                  type="url"
                  id="link"
                  name="link"
                  value={formData.link}
                  onChange={handleInputChange}
                  className={styles.input}
                  placeholder={t(
                    "forms.clientProject.placeholders.projectUrlExample",
                  )}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="websiteUrl" className={styles.label}>
                  {t("forms.clientProject.labels.websiteName")}
                </label>
                <input
                  type="text"
                  id="websiteUrl"
                  name="websiteUrl"
                  value={formData.websiteUrl}
                  onChange={handleInputChange}
                  className={styles.input}
                  placeholder={t(
                    "forms.clientProject.placeholders.websiteNameExample",
                  )}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className={styles.imageSection}>
              <h3 className={styles.sectionSubtitle}>
                {t("forms.clientProject.labels.projectImages")}
              </h3>

              <div className={styles.imageUploadRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="projectImage" className={styles.label}>
                    {t("forms.clientProject.labels.mainProjectImage")} *
                  </label>
                  <div className={styles.fileUploadContainer}>
                    <label
                      className={`${styles.fileInputLabel} ${isSubmitting ? styles.disabled : ""}`}
                    >
                      <span className={styles.fileInputText}>
                        {projectImageFile || formData.projectImage
                          ? "Change Image"
                          : "Choose Image File"}
                      </span>
                      <input
                        type="file"
                        id="projectImage"
                        name="projectImage"
                        ref={projectImageInputRef}
                        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                        onChange={handleProjectImageUpload}
                        className={styles.fileInput}
                        disabled={isSubmitting}
                      />
                    </label>
                    {(projectImagePreview || formData.projectImage) && (
                      <div className={styles.imagePreviewWrapper}>
                        <img
                          src={projectImagePreview || formData.projectImage}
                          alt="Project preview"
                          className={styles.imagePreview}
                        />
                        {(projectImageFile || formData.projectImage) && (
                          <button
                            type="button"
                            onClick={() => {
                              setFormData((p) => ({ ...p, projectImage: "" }));
                              setProjectImagePreview("");
                              setProjectImageFile(null);
                              if (projectImageInputRef.current) {
                                projectImageInputRef.current.value = "";
                              }
                            }}
                            className={styles.removeImageButton}
                            disabled={isSubmitting}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                    {!projectImagePreview && !projectImageFile && (
                      <p className={styles.helperText}>
                        JPEG, PNG, WebP, GIF (max 10MB)
                      </p>
                    )}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="desktopModalImage" className={styles.label}>
                    {t("forms.clientProject.labels.desktopModalImage")} *
                  </label>
                  <div className={styles.fileUploadContainer}>
                    <label
                      className={`${styles.fileInputLabel} ${isSubmitting ? styles.disabled : ""}`}
                    >
                      <span className={styles.fileInputText}>
                        {desktopImageFile || formData.desktopModalImage
                          ? "Change Image"
                          : "Choose Image File"}
                      </span>
                      <input
                        type="file"
                        id="desktopModalImage"
                        name="desktopModalImage"
                        ref={desktopImageInputRef}
                        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                        onChange={handleDesktopImageUpload}
                        className={styles.fileInput}
                        disabled={isSubmitting}
                      />
                    </label>
                    {(desktopImagePreview || formData.desktopModalImage) && (
                      <div className={styles.imagePreviewWrapper}>
                        <img
                          src={
                            desktopImagePreview || formData.desktopModalImage
                          }
                          alt="Desktop preview"
                          className={styles.imagePreview}
                        />
                        {(desktopImageFile || formData.desktopModalImage) && (
                          <button
                            type="button"
                            onClick={() => {
                              setFormData((p) => ({
                                ...p,
                                desktopModalImage: "",
                              }));
                              setDesktopImagePreview("");
                              setDesktopImageFile(null);
                              if (desktopImageInputRef.current) {
                                desktopImageInputRef.current.value = "";
                              }
                            }}
                            className={styles.removeImageButton}
                            disabled={isSubmitting}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                    {!desktopImagePreview && !desktopImageFile && (
                      <p className={styles.helperText}>
                        JPEG, PNG, WebP, GIF (max 10MB)
                      </p>
                    )}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="mobileModalImage" className={styles.label}>
                    {t("forms.clientProject.labels.mobileModalImage")} *
                  </label>
                  <div className={styles.fileUploadContainer}>
                    <label
                      className={`${styles.fileInputLabel} ${isSubmitting ? styles.disabled : ""}`}
                    >
                      <span className={styles.fileInputText}>
                        {mobileImageFile || formData.mobileModalImage
                          ? "Change Image"
                          : "Choose Image File"}
                      </span>
                      <input
                        type="file"
                        id="mobileModalImage"
                        name="mobileModalImage"
                        ref={mobileImageInputRef}
                        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                        onChange={handleMobileImageUpload}
                        className={styles.fileInput}
                        disabled={isSubmitting}
                      />
                    </label>
                    {(mobileImagePreview || formData.mobileModalImage) && (
                      <div className={styles.imagePreviewWrapper}>
                        <img
                          src={mobileImagePreview || formData.mobileModalImage}
                          alt="Mobile preview"
                          className={styles.imagePreview}
                        />
                        {(mobileImageFile || formData.mobileModalImage) && (
                          <button
                            type="button"
                            onClick={() => {
                              setFormData((p) => ({
                                ...p,
                                mobileModalImage: "",
                              }));
                              setMobileImagePreview("");
                              setMobileImageFile(null);
                              if (mobileImageInputRef.current) {
                                mobileImageInputRef.current.value = "";
                              }
                            }}
                            className={styles.removeImageButton}
                            disabled={isSubmitting}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                    {!mobileImagePreview && !mobileImageFile && (
                      <p className={styles.helperText}>
                        JPEG, PNG, WebP, GIF (max 10MB)
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <div className={styles.formActions}>
          <Button
            type="submit"
            variant="default"
            disabled={!selectedProject || isSubmitting || isLoading}
          >
            {isSubmitting ? "Updating..." : "Update Project"}
          </Button>
        </div>
      </form>

      <FeedbackModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        message={modalMessage}
        type={modalType}
        confirmText="Close"
      />
    </>
  );
}
